import { makeStyles, Typography, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import React from 'react'
import { getObjectLabels, TIER_LABELS } from '@paintscout/util/builder'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { useQuote } from '../../../../context'
import { useClientOptions } from '@paintscout/ui-business'

const useStyles = makeStyles(
  (theme: Theme) => {
    const info = theme.paintscout.palette.semantic.Info
    return {
      root: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25),
        borderRadius: theme.borderRadius.lg,
        background: info.S95,
        color: info.S40,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1.5)
      },
      icon: {
        flexShrink: 0,
        fontSize: theme.typography.pxToRem(16),
        marginTop: 2,
        color: info.S70
      },
      text: {
        fontSize: theme.typography.pxToRem(13),
        lineHeight: 1.4,
        color: 'inherit'
      }
    }
  },
  { name: 'PackageApprovedNote' }
)

/**
 * Tells the crew which package the customer approved, at the top of the work order's product
 * description (PROD-292 / ENG-1542).
 *
 * Shown for every estimate with packages enabled, not only ones that swap products: the crew has to
 * know which package they are working to either way. The prototype gates the whole note on swapping,
 * which would leave a Premium-approved job looking identical to a Standard one whenever the account
 * has no product rules.
 *
 * The second sentence is the part that depends on swapping, so it carries the three conditions that
 * make it true: the feature is on for this account, the package set actually carries rules, and the
 * approved package is not the one the estimate was built as.
 *
 * The name is the tier's *positional* label (Basic / Standard / Premium), matching the prototype and
 * the drawer's tier chips — renaming a package does not rename its slot.
 */
export default function PackageApprovedNote() {
  const classes = useStyles()
  const { __ } = useTranslation()
  const { quote } = useQuote()
  const { options } = useClientOptions()
  const { tieredPricingProductSwap } = useFlags()
  const { quote: quoteLabel } = getObjectLabels({ options })
  const tieredPackages = quote?.tieredPackages
  if (!tieredPackages?.enabled) {
    return null
  }

  const { selectedTierId, baseTierId, productRules } = tieredPackages
  const packageLabel = TIER_LABELS[selectedTierId]
  if (!packageLabel) {
    return null
  }

  const productsDiffer = !tieredPricingProductSwap && !!productRules?.length && selectedTierId !== baseTierId

  return (
    <div className={classes.root}>
      <InfoOutlinedIcon className={classes.icon} />
      <Typography component="div" className={classes.text}>
        {__('Customer selected the %s package.', packageLabel)}
        {productsDiffer ? ` ${__('Products may differ from the standard %s.', quoteLabel.value.toLowerCase())}` : ''}
      </Typography>
    </div>
  )
}
